import { Eye, Pencil } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { CatalogModuleView, ProjectSummary } from '../../types'
import { MODULE_CATEGORY_ICON } from '../../visuals'
import { ProjectIcon } from '../ProjectBits'
import { ModulePriceDetailModal } from './ModulePriceDetailModal'
import { ModulePricingModal } from './ModulePricingModal'
import { usePriceFormat } from '../../usePriceFormat'

export function PriceMatrix({ catalog, projects, canWrite }: { catalog: CatalogModuleView[]; projects: ProjectSummary[]; canWrite: boolean }) {
  const { t } = useTranslation()
  const fmtPrice = usePriceFormat()
  const [editing, setEditing] = useState<CatalogModuleView | null>(null)
  const [viewing, setViewing] = useState<CatalogModuleView | null>(null)

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-card">
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH className="min-w-56">{t('projects.catalog.priceList.module')}</TH>
            <TH className="text-right">{t('projects.catalog.priceList.listPrice')}</TH>
            {projects.map((p) => (
              <TH key={p.id} className="text-right">
                <span className="inline-flex items-center gap-1.5">
                  <ProjectIcon icon={p.icon} color={p.color} className="size-5 rounded-md shadow-none" />
                  {p.name}
                </span>
              </TH>
            ))}
            <TH className="w-px whitespace-nowrap text-right">{t('projects.catalog.actions')}</TH>
          </TR>
        </THead>
        <TBody>
          {catalog.map((m) => {
            const Icon = MODULE_CATEGORY_ICON[m.category]
            return (
              <TR key={m.id} className={cn(!m.active && 'text-muted-foreground')}>
                <TD>
                  <div className="flex items-center gap-2">
                    <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0">
                      <span className="block font-medium">{m.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {t(`projects.pricing.${m.pricing}`)}
                        {!m.active ? ` · ${t('projects.catalog.priceList.inactive')}` : ''}
                      </span>
                    </span>
                  </div>
                </TD>
                <TD className="text-right tabular-nums">{fmtPrice(m.price)}</TD>
                {projects.map((p) => {
                  const u = m.usage.find((x) => x.projectId === p.id)
                  if (!u) return <TD key={p.id} className="text-right text-muted-foreground/40">—</TD>
                  const special = u.price !== m.price
                  return (
                    <TD key={p.id} className="text-right">
                      <span className="block tabular-nums">{fmtPrice(u.price)}</span>
                      <span className="mt-0.5 inline-flex items-center gap-1.5 text-[11px]">
                        <span className={cn('size-1.5 rounded-full', u.requirement === 'required' ? 'bg-success' : 'bg-info')} aria-hidden />
                        <span className="text-muted-foreground">{t(`projects.requirement.${u.requirement}`)}</span>
                        {special ? <span className="rounded bg-warning/15 px-1 font-medium text-warning-strong">{t('projects.catalog.priceList.special')}</span> : null}
                      </span>
                    </TD>
                  )
                })}
                <TD>
                  <div className="flex justify-end gap-1">
                    <Tooltip content={t('projects.catalog.priceList.detail')} side="top">
                      <Button size="icon" variant="outline" className="size-8" aria-label={t('projects.catalog.priceList.detailFor', { name: m.name })} onClick={() => setViewing(m)}>
                        <Eye />
                      </Button>
                    </Tooltip>
                    {canWrite ? (
                      <Tooltip content={t('projects.catalog.priceList.edit')} side="top">
                        <Button size="icon" variant="outline" className="size-8" aria-label={t('projects.catalog.priceList.editFor', { name: m.name })} onClick={() => setEditing(m)}>
                          <Pencil />
                        </Button>
                      </Tooltip>
                    ) : null}
                  </div>
                </TD>
              </TR>
            )
          })}
        </TBody>
      </Table>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-4 py-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-success" aria-hidden />
          {t('projects.catalog.priceList.legendRequired')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-info" aria-hidden />
          {t('projects.catalog.priceList.legendOptional')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="rounded bg-warning/15 px-1 font-medium text-warning-strong">{t('projects.catalog.priceList.special')}</span>
          {t('projects.catalog.priceList.legendSpecial')}
        </span>
        <span>— {t('projects.catalog.priceList.legendNone')}</span>
      </div>

      {viewing ? (
        <ModulePriceDetailModal
          module={viewing}
          projects={projects}
          canWrite={canWrite}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setEditing(viewing)
            setViewing(null)
          }}
        />
      ) : null}
      {editing ? <ModulePricingModal module={editing} projects={projects} onClose={() => setEditing(null)} /> : null}
    </div>
  )
}
